import React from 'react';
import { useParams } from 'react-router-dom';

import {
  Accordion,
  Badge,
  Box,
  Button,
  Divider,
  Grid,
  Group,
  Pagination,
  Text
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';

import { statusMap } from '@/store/selectors/transmissionReportSelectors';
import { useGetPatientQuery } from '@/store/slices/api/patientsApiSlice';
import formatDate from '@/utils/formatDate';

import { MatchDevice } from '@/types/matchDevice';
import IconButton from '@/ui/IconButton';
import LabeledField from '@/ui/LabeledField';

import DeviceAddModal from '../../TransmissionReportPage/DeviceInfo/DeviceAddModal';
import DeviceEditModal from '../../TransmissionReportPage/DeviceInfo/DeviceEditModal';
import EnrollmentGapIndicator from '../EnrollmentGapIndicator';
import { VENDOR_MAPPING } from '../types';

import styles from './Devices.module.scss';
import LeadManager from './LeadManager';

const getDeviceFields = (device: MatchDevice) => {
  const isLeadlessPM =
    (device.modelNumber ?? '').includes(':') || (device.serialNumber ?? '').includes(':');
  const [modelNumber1, modelNumber2] = isLeadlessPM
    ? (device.modelNumber ?? '').split(':')
    : [device.modelNumber, undefined];
  const [serialNumber1, serialNumber2] = isLeadlessPM
    ? (device.serialNumber ?? '').split(':')
    : [device.serialNumber, undefined];

  const baseFields = [
    { label: 'Manufacturer', value: device.vendor?.toUpperCase() },
    { label: 'Type', value: isLeadlessPM ? 'Leadless PM' : device.type },
    { label: 'Model Name', value: device.modelName }
  ];

  const modelSerialFields = isLeadlessPM
    ? [
        { label: 'Model Number 1', value: modelNumber1 },
        { label: 'Model Number 2', value: modelNumber2 },
        { label: 'Serial Number 1', value: serialNumber1 },
        { label: 'Serial Number 2', value: serialNumber2 }
      ]
    : [
        { label: 'Model Number', value: device.modelNumber },
        { label: 'Serial Number', value: device.serialNumber }
      ];

  return [
    ...baseFields,
    ...modelSerialFields,
    { label: 'Implant Date', value: formatDate(device.implantDate, 'MM/DD/YYYY') },
    { label: 'Device Telemetry', value: device.deviceTelemetry },
    {
      label: 'MRI Compatibility',
      value: device.isMriCompatibility == null ? '-' : device.isMriCompatibility ? 'Yes' : 'No'
    }
  ];
};

const connectivityLabel = (status?: MatchDevice['connectivityStatus']) => {
  if (status === 'Connected' || status === 'Inductive') return { label: status, color: 'teal' };
  if (status === 'Disconnected') return { label: 'Disconnected', color: 'red' };
  return { label: 'Connectivity unknown', color: 'gray' };
};

const renderFields = (fields: { label: string; value?: string | null }[]) => {
  return fields.map((field) => (
    <Grid.Col key={field.label} span={{ base: 12, sm: 6, md: 3 }}>
      <LabeledField labelVariant="sentence" label={field.label} value={field.value} />
    </Grid.Col>
  ));
};

interface DevicesProps {
  isDeviceModalOpen: boolean;
  setIsDeviceModalOpen: (open: boolean) => void;
}

const Devices: React.FC<DevicesProps> = ({ isDeviceModalOpen, setIsDeviceModalOpen }) => {
  const { uuid: patientId } = useParams() as { uuid: string };
  const { data: patient, refetch } = useGetPatientQuery(patientId);

  const [editModalOpened, setEditModalOpened] = React.useState(false);
  const [selectedDevice, setSelectedDevice] = React.useState<MatchDevice | undefined>(undefined);
  const [openedAccordion, setOpenedAccordion] = React.useState<string | null>(null);
  const [activePage, setActivePage] = React.useState(1);
  const [inactivePage, setInactivePage] = React.useState(1);
  const devicePageSize = 10;

  const devices = patient?.matchDevices;

  const handleModalClose = () => {
    setEditModalOpened(false);
    refetch();
  };

  const handleAddModalClose = () => {
    setIsDeviceModalOpen(false);
    refetch();
  };

  const isActiveDevice = (device: MatchDevice) => {
    const status = String(device.status ?? '').toLowerCase();
    return !(
      status === 'inactive' ||
      status === 'entered in error' ||
      status === '0' ||
      status === '4'
    );
  };

  if (!devices || devices.length === 0) {
    return (
      <>
        <Group justify="space-between" align="center" mb="xs">
          <Group gap={6} wrap="nowrap">
            <Text className={styles.sectionHeading}>Active Devices</Text>
            <EnrollmentGapIndicator section="device" />
          </Group>
          <Button
            size="xs"
            variant="subtle"
            leftSection={<IconPlus size={16} />}
            onClick={() => setIsDeviceModalOpen(true)}>
            Add Device
          </Button>
        </Group>
        <Text size="sm">Patient has no active devices.</Text>
        <DeviceAddModal
          isOpen={isDeviceModalOpen}
          onClose={handleAddModalClose}
          patientId={patientId}
          devices={patient?.matchDevices || []}
          lockDeviceTelemetry
        />
      </>
    );
  }

  const activeDevices = devices.filter(isActiveDevice);
  const inactiveDevices = devices.filter((device) => !isActiveDevice(device));

  const renderDeviceItem = (device: MatchDevice) => (
    <Accordion.Item
      value={String(device.id)}
      key={device.id}
      className={styles.deviceAccordionItem}>
      <Accordion.Control>
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
            <Text fw={500} className={styles.accTab} truncate>
              {VENDOR_MAPPING[device.vendor] ?? device.vendor} {device.type}
            </Text>
            <Badge
              variant="light"
              radius="sm"
              size="sm"
              color={isActiveDevice(device) ? 'teal' : 'gray'}
              tt="none">
              {isActiveDevice(device) ? 'Active' : 'Inactive'}
            </Badge>
            {device.isEriTracked && (
              <Badge variant="light" color="orange" radius="sm" size="sm" tt="none">
                ERI Tracking
              </Badge>
            )}
            <Badge
              variant="light"
              radius="sm"
              size="sm"
              color={connectivityLabel(device.connectivityStatus).color}
              tt="none">
              {connectivityLabel(device.connectivityStatus).label}
            </Badge>
          </Group>
          <Group gap={10} wrap="nowrap" style={{ flexShrink: 0 }}>
            <Text size="xs" c="dimmed" fw={500}>
              Last transmission{' '}
              {device.lastTransmissionAt
                ? formatDate(device.lastTransmissionAt, 'MM/DD/YYYY')
                : '—'}
            </Text>
            {openedAccordion === String(device.id) && (
              <div onClick={(e) => e.stopPropagation()}>
                <IconButton
                  icon="edit"
                  label="Edit"
                  onClick={() => {
                    setSelectedDevice({
                      ...device,
                      status:
                        statusMap[device?.status as unknown as 0 | 1 | 2 | 3 | 4] ?? device?.status
                    });
                    setEditModalOpened(true);
                  }}
                />
              </div>
            )}
          </Group>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        {/* Device Details */}
        <Box mb="sm" mt={'5px'}>
          <Text fw={600}>Device Details</Text>
          <Divider my={4} />
          <Grid mt="md">{renderFields(getDeviceFields(device))}</Grid>
        </Box>
        {device?.type !== 'ILR' && (
          <>
            <Divider my="sm" />
            <LeadManager
              deviceId={String(device.id)}
              patientId={patientId}
              seedLeads={device.leads}
              onSaved={() => refetch()}
            />
          </>
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );

  const pageOf = (items: MatchDevice[], page: number) =>
    items.slice((page - 1) * devicePageSize, page * devicePageSize);

  const devicePager = (count: number, page: number, onChange: (next: number) => void) => {
    const pages = Math.ceil(count / devicePageSize);
    if (pages <= 1) return null;
    return (
      <Group justify="center" mt="sm">
        <Pagination total={pages} value={page} onChange={onChange} size="sm" color="plum" />
      </Group>
    );
  };

  const renderAccordion = (items: MatchDevice[]) => (
    <Accordion
      variant="separated"
      className={styles.deviceAccordion}
      chevronPosition="left"
      value={openedAccordion}
      onChange={setOpenedAccordion}>
      {items.map(renderDeviceItem)}
    </Accordion>
  );

  return (
    <>
      <Group justify="space-between" align="center" mb="xs">
        <Group gap={6} wrap="nowrap">
          <Text className={styles.sectionHeading}>Active Devices</Text>
          <EnrollmentGapIndicator section="device" />
        </Group>
        <Button
          size="xs"
          variant="subtle"
          leftSection={<IconPlus size={16} />}
          onClick={() => setIsDeviceModalOpen(true)}>
          Add Device
        </Button>
      </Group>
      {activeDevices.length > 0 ? (
        <Box mb="lg">
          {renderAccordion(pageOf(activeDevices, activePage))}
          {devicePager(activeDevices.length, activePage, setActivePage)}
        </Box>
      ) : (
        <Text size="sm" c="dimmed" mb="lg">
          No active devices.
        </Text>
      )}
      {inactiveDevices.length > 0 && (
        <Box>
          <Text className={styles.sectionHeading} mb="xs">
            Inactive Devices
          </Text>
          {renderAccordion(pageOf(inactiveDevices, inactivePage))}
          {devicePager(inactiveDevices.length, inactivePage, setInactivePage)}
        </Box>
      )}
      <DeviceEditModal
        isOpen={editModalOpened}
        onClose={handleModalClose}
        deviceInfo={selectedDevice}
        leadsData={selectedDevice?.leads || []}
        isPatientView={true}
      />
      <DeviceAddModal
        isOpen={isDeviceModalOpen}
        onClose={handleAddModalClose}
        patientId={patientId}
        devices={patient?.matchDevices || []}
        lockDeviceTelemetry
      />
    </>
  );
};

export default Devices;
